/**
 * Auth bootstrap — syncs token and fetches current user/role from server.
 * Ensures RBAC guard has authoritative roleCatalog and role from backend, not just localStorage.
 *
 * Call before app mount or on first navigation.
 */
import { TokenService } from '@/shared/http/token';
import http from '@/shared/http';
import { normalizeRole } from '@/shared/auth/rbac';
import { AI_FEATURES, AI_PACKAGE_FEATURE_KEYS } from '@/shared/constants/packageFeatures';

function deriveEnabledFeaturesFromPackage(aiPackageSettings) {
  const knownFeatureKeys = new Set(Object.values(AI_PACKAGE_FEATURE_KEYS));
  if (!Array.isArray(aiPackageSettings)) return [];
  return aiPackageSettings
    .filter((feature) => Boolean(feature?.enabled ?? feature?.isEnabled ?? false))
    .map((feature) => {
      const mapped = AI_FEATURES[feature?.featureId];
      if (mapped) return mapped;
      const raw = String(feature?.featureId || '').trim();
      return knownFeatureKeys.has(raw) ? raw : null;
    })
    .filter(Boolean);
}

/**
 * Ensures auth store has token and userRole from server.
 * - Syncs TokenService token to auth store if auth is empty
 * - Fetches /api/auth/me when we have token to get fresh role and roleCatalog from backend
 *
 * @param {import('vuex').Store} store
 * @returns {Promise<void>}
 */
export async function ensureAuthState(store) {
  const auth = store.state.auth;
  const token = TokenService.getToken() || auth?.token;

  // Sync token: if TokenService has it but auth doesn't, sync to auth
  if (token && !auth?.token) {
    store.commit('auth/SET_TOKEN', token);
  }
  if (auth?.token && !TokenService.getToken()) {
    TokenService.setToken(auth.token);
  }

  // No token — nothing to bootstrap
  if (!token && !auth?.token) {
    return;
  }

  // Token expired — clear and bail
  if (TokenService.isTokenExpired()) {
    await store.dispatch('auth/logout');
    return;
  }

  // Fetch current user from server for authoritative role + roleCatalog
  try {
    const { data } = await http.get('/auth/me');
    const user = data?.user;
    const roleCatalog = data?.roleCatalog || null;
    if (!user) return;

    store.commit('auth/SET_ROLE_CATALOG', roleCatalog);

    const incomingRole = typeof user?.roles?.[0]?.role === 'string'
      ? user.roles[0].role.trim()
      : user?.roles?.[0]?.role;
    const userRole = normalizeRole(incomingRole, roleCatalog);
    const allowed =
      roleCatalog?.roles && roleCatalog.roles.includes(userRole);
    if (!allowed) return;

    store.commit('auth/SET_USER_ROLE', userRole);
    store.commit('auth/SET_USER_ID', user.id || '');
    store.commit('auth/SET_INSTITUTION_ID', user.institutionId || '');
    store.commit('auth/SET_FIRST_NAME', user.firstName || user.name?.split?.(' ')?.[0] || 'User');
    store.commit('auth/SET_USER_PROFILE_ID', user.userProfileId || '');
    store.commit('auth/SET_AI_ADMIN', user.aiAdmin || false);
    store.commit('auth/SET_INSTITUTION_LOGO', user.institutionLogo || '');
    store.commit('auth/SET_INSTITUTION_DISPLAY_NAME', user.institutionDisplayName || '');
    const aiPackageSettings = data?.aiPackageSettings || user?.aiPackageSettings || [];
    store.commit('auth/SET_AI_PACKAGE_SETTINGS', aiPackageSettings);
    store.commit('auth/SET_ENABLED_FEATURES', user.aiFeatures || deriveEnabledFeaturesFromPackage(aiPackageSettings));

    store.commit('setUserInstitutionId', user.institutionId || '');
    store.commit('setUserId', user.id || '');
    store.commit('setUserRole', userRole);
    store.commit('setUserProfileId', user.userProfileId || '');
    store.commit('setFirstName', user.firstName || user.name?.split?.(' ')?.[0] || 'User');
    store.commit('setInstitutionLogo', user.institutionLogo || '');
    store.commit('setUserEnrolledCourses', []);
  } catch (err) {
    if (err?.response?.status === 401 || err?.response?.status === 403) {
      await store.dispatch('auth/logout');
    }
  }
}
